import { useEffect, useRef } from 'react';
import { useQueryStates } from 'nuqs';
import { feedIdFromRouteId, parseRequestRouteId, routeIdFromFeedId } from '@shared/flow/requests';
import { FLOW_REQUEST_URL_PARAM, flowChatParams, flowRequestParams } from '../../../lib/urlParams';
import { openPanel, useFlowPanelsStore } from '../shell-next/panels/flow-panels-store';
import { useShellFrameStore } from '../shell-next/shell-frame-store';
import { useEnrollQueue } from './EnrollQueueContext';
import { useFlowChatSessions, useFlowChatSessionList } from './FlowChatSessionsProvider';

/**
 * The dock's URL<->state sync, called by `shell-next/rail/RailDock` (it began
 * life in the now-deleted V1 `EnrollQueueSidebar`; the comments below are the
 * original's). The chat READ effect opens the V2 Chat PANEL rather than a dock
 * tab — `RailDock` is the one caller.
 *
 * Two contracts this hook carries (see `docs/flow/request-deep-links.md`):
 *
 *  - It must be called by a component that is mounted REGARDLESS of
 *    `activeTab` and BEFORE any `if (!isOpen) return null` early return — a
 *    pasted `?request=` link has to be able to switch the tab (and open the
 *    dock) in the first place, and a pasted `?flowChat=` link has to be able
 *    to open the Chat panel.
 *  - EXACTLY ONE caller may be mounted at a time. Two callers would be two
 *    writers on one URL — the collision the nuqs migration below exists to
 *    kill. `RailDock` is the only caller.
 *
 * Returns `void`: the sidebar's JSX reads nothing out of this block (the
 * `?request=` param's only consumers are the two effects here).
 */
export function useDockUrlSync(): void {
  const { isOpen, activeTab, selectedRequestId, openRequestDetail } = useEnrollQueue();

  // Shareable/deep-linkable chat URLs (round-4 item 7, 2026-08-20).
  // This is the ONLY place either direction of the URL<->chat sync lives.
  // It has to be HERE, not in the chat panel (which only mounts once the panel
  // is already open): a pasted link needs to be able to OPEN the panel in the
  // first place, which requires a component that's always mounted. The dock
  // shell is exactly that (mounted once at shell level), and — since it sits
  // inside `FlowChatSessionsProvider` (see AppRouter.tsx's nesting order) — it
  // can reach `useFlowChatSessions()` (focus/create the session) and the panel
  // store (open the panel) in one place.
  const { activeSessionId, focusOrCreateSessionForConversation } = useFlowChatSessions();
  const chatSessions = useFlowChatSessionList();
  const activeChatEntry = activeSessionId ? chatSessions.find((s) => s.sessionId === activeSessionId) : undefined;
  // A session exists from the moment it's created, but its `conversationId`
  // stays `null` until the first message round-trips (see
  // `FlowChatSessionsProvider.tsx`'s `entries` map) — nothing durable exists
  // on the server yet, so there's nothing shareable to put in the URL.
  const activeConversationId = activeChatEntry?.conversationId ?? null;
  const activeAccountId = activeChatEntry?.accountId ?? null;

  // GTMOPS-2285 Track A: these two params are nuqs-owned (`flowChatParams`,
  // `lib/urlParams.ts`), NOT react-router's `useSearchParams`. Prior to this
  // fix, the WRITE branch below rebuilt the ENTIRE query string via
  // `new URLSearchParams(searchParams)` + react-router's `setSearchParams` —
  // exactly the two-writers-one-URL collision `useViewStateMachine.ts`'s own
  // doc comment documents for `?view=` vs `?tab=`: `KeystoneChatPanel.tsx`
  // writes `keystoneConversationId` through nuqs's `shallow: true` (a bare
  // `history.replaceState` react-router's `useSearchParams` snapshot never
  // observes), so react-router's stale snapshot silently dropped
  // `keystoneConversationId` on every dock open/close/session-switch — the
  // account-view chat visually reset mid-conversation. Rebasing the
  // react-router write on `window.location` instead does NOT fix it (same
  // doc comment: the router write still trips nuqs's history-patch resync).
  // One shared writer (nuqs, here) is the actual fix — `setChatParams` only
  // ever touches its own two keys, so it can never clobber a param owned by
  // a different nuqs consumer.
  const [{ flowChat: urlConversationId, flowChatAccount: urlAccountId }, setChatParams] =
    useQueryStates(flowChatParams, { history: 'replace' });

  // WRITE direction — local chat state -> URL. Runs only when LOCAL state
  // changes (active session / panel). It deliberately does
  // NOT re-run when the url's own params change: an incoming URL (a pasted or
  // in-app link) is the READ effect's input, and a WRITE that "corrected" it
  // back to this render's not-yet-updated session snapshot would fight READ
  // forever (A -> B -> A ...). The URL's current value is read through a ref
  // for the no-op comparison instead.
  //
  // The chat PANEL (framed shell's chat column) is the only chat surface: the
  // READ effect below opens it for a pasted `?flowChat=` link, so the params
  // persist while the panel is open on a framed route — otherwise this effect
  // would clear the very link that just opened it. Closing the panel (or
  // leaving the framed shell) clears them. The unframed Delphi route has no
  // chat surface, so the params never persist there.
  const chatPanelOpen = useFlowPanelsStore((s) => s.chatOpen);
  const framed = useShellFrameStore((s) => s.frameActive);
  const urlChatRef = useRef({ conversationId: urlConversationId, accountId: urlAccountId });
  urlChatRef.current = { conversationId: urlConversationId, accountId: urlAccountId };
  useEffect(() => {
    const { conversationId: urlConv, accountId: urlAcct } = urlChatRef.current;
    const chatSurfaceOpen = framed && chatPanelOpen;
    const shouldHaveParams = chatSurfaceOpen && activeConversationId !== null && activeAccountId !== null;
    if (shouldHaveParams) {
      if (urlConv === activeConversationId && urlAcct === activeAccountId) return;
      void setChatParams({ flowChat: activeConversationId, flowChatAccount: activeAccountId });
    } else {
      if (urlConv === null && urlAcct === null) return;
      void setChatParams({ flowChat: null, flowChatAccount: null });
    }
  }, [framed, chatPanelOpen, activeConversationId, activeAccountId, setChatParams]);

  // RESTORE: an in-app navigate that replaced the whole query string (NavRail's
  // `?tab=`, header search) strips the chat params while the panel still shows
  // the chat, and WRITE's deps (deliberately) exclude the URL params, so
  // nothing re-runs it. Re-run only when the URL goes EMPTY; a non-empty URL is
  // READ's input. No loop: RESTORE acts only on an empty URL (READ ignores it)
  // and READ only on a non-empty one. Panel close / back-to-list / a draft
  // session (null ids): WRITE clears the URL and RESTORE bails on the live ref.
  const urlChatEmpty = urlConversationId === null && urlAccountId === null;
  const localChatRef = useRef({ open: framed && chatPanelOpen, activeConversationId, activeAccountId });
  localChatRef.current = { open: framed && chatPanelOpen, activeConversationId, activeAccountId };
  useEffect(() => {
    if (!urlChatEmpty) return;
    const { open, activeConversationId: conv, activeAccountId: acct } = localChatRef.current;
    if (!open || conv === null || acct === null) return;
    void setChatParams({ flowChat: conv, flowChatAccount: acct });
  }, [urlChatEmpty, setChatParams]);

  // READ direction — URL -> local chat state (restore-on-load/paste). The
  // local session store (`FlowChatSessionsProvider`'s `entries` map) is
  // in-memory only (no localStorage/sessionStorage backing it — confirmed,
  // there is none), so this is NOT an edge case: it's the primary path a
  // pasted link takes on a fresh tab after a real page refresh.
  //
  // Loop-safety: deliberately scoped (via the dependency array below) to
  // re-run ONLY when the URL's OWN two params change, never merely because
  // `activeConversationId` changed locally — e.g. clicking "Back to all
  // chats" sets the active session to `null` while the URL still has the
  // OLD session's params for one render; if this effect reactively watched
  // `activeConversationId`, that mismatch (url's id !== null) would look
  // identical to "a pasted link arrived" and would re-open the very session
  // the rep just backed out of. Scoping to the url's own params means nothing
  // here fires on a "back to list" render at all — the WRITE effect above is
  // the only one that reacts to it, and clears the (now-stale) params.
  // Within the effect body, the guard is still a pure value comparison (no
  // "did I just write it" ref) — matches or is empty, no-op regardless of
  // which direction most recently changed something.
  //
  // `focusOrCreateSessionForConversation` is a NEW closure on every call to
  // `useFlowChatSessions()` (it wraps the store method inline, per that
  // file's own "never a bare method reference" convention) — listing it in
  // this effect's deps would make the effect re-run on EVERY render
  // regardless of the url, silently defeating the scoping above (found via a
  // real regression: it read that render's already-updated `null`
  // `activeConversationId` against the not-yet-cleared, stale
  // `urlConversationId`, and incorrectly restored the very session "Back to
  // all chats" had just backed out of). Latest-ref indirection keeps the
  // deps array truthful to "only the url matters" while still calling the
  // CURRENT function.
  const focusOrCreateSessionForConversationRef = useRef(focusOrCreateSessionForConversation);
  focusOrCreateSessionForConversationRef.current = focusOrCreateSessionForConversation;
  useEffect(() => {
    if (!urlConversationId || !urlAccountId) return;
    if (urlConversationId === activeConversationId) return;
    focusOrCreateSessionForConversationRef.current(urlAccountId, urlConversationId, '');
    // A pasted `?flowChat=` link lands in the Chat PANEL
    // (`shell-next/panels/FlowPanels.tsx`'s chat column, opened via
    // `openPanel('chat')`), which reads the session the link named via
    // `focusOrCreateSessionForConversation` above.
    openPanel('chat');
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- intentionally NOT reactive to `activeConversationId` or the two ref-wrapped callbacks — see the doc comment above (real eslint-plugin-react-hooks does not flag this array; only oxlint's stricter reimplementation does, hence the oxlint-scoped directive rather than a shared eslint-disable, which ESLint would report as unused).
  }, [urlConversationId, urlAccountId]);

  // Shareable/deep-linkable REQUEST urls (U13, plan D2) — `?request=<route id>`.
  // The in-app mirror of the bare `/flow/requests/:requestId` route
  // (`RequestDeepLinkRoute.tsx`): while a request detail is open on the
  // Actions surface, the address bar itself is pasteable, and pasting that
  // address back re-opens the same detail.
  //
  // Same three structural rules as the chat pair above, for the same
  // reasons: (1) it lives HERE, not in `RequestsTab`, because a pasted param
  // must be able to SWITCH to the Actions tab in the first place, and the
  // dock shell is the only thing mounted regardless of `activeTab`; (2) the
  // param is nuqs-owned — NEVER `useSearchParams`, whose snapshot cannot see
  // another nuqs consumer's `shallow: true` history patch and would silently
  // drop keys it never observed (the GTMOPS-2285 clobber bug, pinned by the
  // "never clobbers another nuqs consumer's param" test in
  // RequestsTab.test.tsx); (3) the READ effect is scoped to the URL's OWN
  // param so "Back to Actions" (which nulls `selectedRequestId` while the
  // param is still set for one render) cannot be mistaken for a pasted link
  // and re-open the detail the rep just left.
  //
  // The value is the ROUTE spelling (`routeIdFromFeedId`: uuid → `ex-<uuid>`,
  // `ei-<id>` unchanged) — identical to what the bare route, "Copy link" and
  // `terra_signals.source_link` carry, so all four are one string.
  const [{ [FLOW_REQUEST_URL_PARAM]: urlRequestRouteId }, setRequestParams] =
    useQueryStates(flowRequestParams, { history: 'replace' });

  // WRITE direction — dock state -> URL. The param exists exactly while a
  // detail is on screen: the dock open, Actions the active surface, and a
  // selection with a shareable spelling. Every other state (dock closed,
  // another tab, Back to the list, a feed id with no route form) clears it,
  // which is also what makes `closeRequestDetail` clear the URL without the
  // detail view needing to know the URL exists.
  useEffect(() => {
    const shouldHaveParam = isOpen && activeTab === 'requests' && selectedRequestId !== null;
    const desired = shouldHaveParam ? routeIdFromFeedId(selectedRequestId) : null;
    if (desired === urlRequestRouteId) return;
    void setRequestParams({ [FLOW_REQUEST_URL_PARAM]: desired });
  }, [isOpen, activeTab, selectedRequestId, urlRequestRouteId, setRequestParams]);

  // READ direction — URL -> dock state (restore-on-load/paste). The dock's
  // selection is in-memory only, so this is the primary path for a reloaded
  // or pasted `?request=` URL, not an edge case. An unparseable value is
  // ignored (never fetched) and the WRITE effect above then clears it rather
  // than leaving a lie in the address bar.
  //
  // `selectedRequestId` is read through a ref rather than being a dependency
  // for the "Back to Actions" reason in the block comment above; the callback
  // is ref-wrapped because the context value is a new object on many renders.
  const openRequestDetailRef = useRef(openRequestDetail);
  openRequestDetailRef.current = openRequestDetail;
  const selectedRequestIdRef = useRef(selectedRequestId);
  selectedRequestIdRef.current = selectedRequestId;
  useEffect(() => {
    if (!urlRequestRouteId) return;
    if (selectedRequestIdRef.current !== null) return;
    const parsed = parseRequestRouteId(urlRequestRouteId);
    if (parsed === null) return;
    openRequestDetailRef.current(feedIdFromRouteId(parsed));
  }, [urlRequestRouteId]);
}
